import { Routes, Route, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { MainLayout } from '@/components/layout'
import { useAuth } from '@/contexts/AuthContext'
import { useUnit } from '@/contexts/UnitContext'
import { BillingProvider } from '@/contexts/BillingContext'
import { getProfile } from '@/lib/services/profiles'
import { getUserUnitsByContext } from '@/lib/services/units'
import { BillingDashboard } from '@/components/billing/BillingDashboard'
import { FinancialEntriesPage } from '@/components/billing/FinancialEntriesPage'
import { ContactsPage } from '@/components/billing/ContactsPage'

export default function BillingLayout() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { currentUnit, isLoading: unitLoading, setCurrentUnit: switchUnit } = useUnit()
  const [profile, setProfile] = useState<any | null>(null)
  const [resolving, setResolving] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!user?.id) return
    getProfile(user.id)
      .then(setProfile)
      .catch((err) => console.error('Error loading profile in BillingLayout', err))
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) return
    if (unitLoading || resolving) return

    // Current unit already belongs to billing — all good
    if (currentUnit?.context_key === 'billing') {
      setReady(true)
      return
    }

    // Current unit is missing or belongs to another context.
    // Look for any billing unit the user owns and switch to it.
    const resolve = async () => {
      setResolving(true)
      try {
        const billingUnits = await getUserUnitsByContext(user.id, 'billing')
        if (billingUnits.length > 0) {
          await switchUnit(billingUnits[0].id)
          setReady(true)
        } else {
          navigate('/onboarding', { replace: true })
        }
      } catch (err) {
        console.error('[BillingLayout] Error resolving billing unit:', err)
        navigate('/onboarding', { replace: true })
      } finally {
        setResolving(false)
      }
    }

    resolve()
  }, [user?.id, unitLoading, currentUnit])

  if (unitLoading || resolving || !ready) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent" />
      </div>
    )
  }

  return (
    <MainLayout
      userName={profile?.full_name || user?.email}
      userId={user?.id}
      currentUnitId={profile?.current_unit_id || null}
    >
      <BillingProvider>
        <Routes>
          <Route index element={<BillingDashboard />} />
          <Route path="receivable" element={<FinancialEntriesPage entryType="receivable" />} />
          <Route path="payable"    element={<FinancialEntriesPage entryType="payable" />} />
          <Route path="contacts"   element={<ContactsPage />} />
        </Routes>
      </BillingProvider>
    </MainLayout>
  )
}
